import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  InputGroup,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  collection,
  collectionGroup,
  query,
  where,
  getDoc,
  doc,
  setDoc,
  getFirestore,
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isWeekend,
  isToday,
  addDays,
  subDays,
  differenceInCalendarDays,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  FaCalendarAlt,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaSave,
  FaUserCog,
  FaSyncAlt,
  FaClock,
  FaFileExcel,
  FaDownload,
} from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";
import * as XLSX from "xlsx";
import { canSaveRolAssignment, resolveRolSaveSedeId } from "./rolTurnosUtils";

const db = getFirestore(firebaseApp);
const REGIMENES_FALLBACK = [
  { id: "PERSONALIZADO", nombre: "Personalizado", diasTrabajo: null, diasDescanso: null },
  { id: "5x2", nombre: "Turno 5x2", diasTrabajo: 5, diasDescanso: 2 },
  { id: "14x14", nombre: "Turno 14x14", diasTrabajo: 14, diasDescanso: 14 },
  { id: "21x14", nombre: "Turno 21x14", diasTrabajo: 21, diasDescanso: 14 },
  { id: "24x14", nombre: "Turno 24x14", diasTrabajo: 24, diasDescanso: 14 },
];
const EXCEPTION_CATALOG = {
  T: { code: "T", estado: "Trabajo", label: "Trabajo", bgColor: "#e0f2fe", textColor: "#0284c7" },
  N: { code: "N", estado: "Trabajo Noche", label: "T. Noche", bgColor: "#dbeafe", textColor: "#1d4ed8" },
  D: { code: "D", estado: "Descanso", label: "Descanso", bgColor: "#f1f5f9", textColor: "#475569" },
  C: { code: "C", estado: "Cese", label: "Cese", bgColor: "#e2e8f0", textColor: "#334155" },
  M: { code: "M", estado: "Descanso Médico", label: "Desc. Médico", bgColor: "#fee2e2", textColor: "#dc2626" },
  V: { code: "V", estado: "Vacaciones", label: "Vacaciones", bgColor: "#dcfce7", textColor: "#16a34a" },
  F: { code: "F", estado: "Falta", label: "Falta", bgColor: "#ffe4e6", textColor: "#be123c" },
  PG: { code: "PG", estado: "Permiso con Goce", label: "Perm. c/goce", bgColor: "#ede9fe", textColor: "#6d28d9" },
  PS: { code: "PS", estado: "Permiso sin Goce", label: "Perm. s/goce", bgColor: "#fef3c7", textColor: "#92400e" },
  LP: { code: "LP", estado: "Licencia por Paternidad", label: "Lic. Paternidad", bgColor: "#ddd6fe", textColor: "#5b21b6" },
  LO: { code: "LO", estado: "Licencia Otros", label: "Lic. Otros", bgColor: "#fce7f3", textColor: "#9f1239" },
  P: { code: "P", estado: "Permiso", label: "Permiso", bgColor: "#f3e8ff", textColor: "#9333ea" },
};
const EXCEPTION_LEGEND_ORDER = ["T", "N", "D", "C", "M", "V", "F", "PG", "PS", "LP", "LO"];

const RolTurnos = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";

  // ═══════════════════════════════════════════════════════════════════
  // ESTADO
  // ═══════════════════════════════════════════════════════════════════
  // P0: Estado de carga explícita — NO se carga automáticamente
  const [rolCargado, setRolCargado] = useState(false);
  const [cargandoRol, setCargandoRol] = useState(false);

  const [empleados, setEmpleados] = useState([]);
  const [rolesMap, setRolesMap] = useState({});
  const [membershipsMap, setMembershipsMap] = useState({});
  const [sedesEmpresa, setSedesEmpresa] = useState([]);
  const [scopeSedeIds, setScopeSedeIds] = useState([]);
  const [selectedSedeId, setSelectedSedeId] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [mesActual, setMesActual] = useState(new Date());
  // GRUPO como eje principal de autorización
  const [authorizedGroups, setAuthorizedGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("ALL");

  // Modal Configuración
  const [showModal, setShowModal] = useState(false);
  const [empModal, setEmpModal] = useState(null);
  const [configTemp, setConfigTemp] = useState({
    fechaInicio: format(new Date(), "yyyy-MM-dd"),
    diasTrabajo: 24,
    diasDescanso: 14,
    regimenId: "PERSONALIZADO",
    regimenNombre: "Personalizado",
  });
  const [guardando, setGuardando] = useState(false);
  const [regimenesBase, setRegimenesBase] = useState(REGIMENES_FALLBACK);

  // Modal Ajuste (celda)
  const [showAdjModal, setShowAdjModal] = useState(false);
  const [adjData, setAdjData] = useState(null);
  const [adjConfig, setAdjConfig] = useState({ type: "Trabajo", num: 1 });
  const [adjFlex, setAdjFlex] = useState({ dias: 1, compensacion: "DESCANSO", motivo: "" });
  const [adjShiftDays, setAdjShiftDays] = useState(1);
  const [adjContext, setAdjContext] = useState({ factor: "CLIMA", detalle: "" });
  // P1: Eliminado refreshKey — React actualiza directamente vía state

  const [attendanceStatusMap, setAttendanceStatusMap] = useState({});
  // P2: Cache de marcaciones por mes (ref para evitar re-renders)
  const attendanceCacheRef = useRef({});

  // ═══════════════════════════════════════════════════════════════════
  // FUNCIONES AUXILIARES
  // ═══════════════════════════════════════════════════════════════════
  const getRolDocRef = (dni, sedeId) =>
    doc(db, "pl_rol_turnos", String(rucEmpresa), "sedes", String(sedeId || "general"), "rol", String(dni));
  const getRolLegacyDocRef = (dni) =>
    doc(db, "pl_rol_turnos", String(rucEmpresa), "rol", String(dni));
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));
  const normalizeRole = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  const isEmpresaAdminRole = (role) => {
    const normalized = normalizeRole(role);
    return ["admin", "superadmin", "administrador", "super administrador"].includes(normalized);
  };
  const getExceptionMeta = (code) =>
    EXCEPTION_CATALOG[code] || { code, estado: "Excepción", label: code, bgColor: "#f8fafc", textColor: "#475569" };
  const unique = (arr) => Array.from(new Set((arr || []).filter(Boolean).map((v) => String(v))));
  const chunkValues = (values, size = 10) => {
    const chunks = [];
    for (let i = 0; i < values.length; i += size) {
      chunks.push(values.slice(i, i + size));
    }
    return chunks;
  };
  const resolveSedeForEmpleado = (dni, preferredSedeId = null) => {
    const memberSedes = unique(membershipsMap[String(dni)]?.sedeIds || []);
    return resolveRolSaveSedeId({
      dni,
      memberships: memberSedes,
      selectedSedeId,
      scopeSedeIds,
      rolesMap,
      preferredSedeId,
    });
  };

  const persistRolData = async (dni, data, preferredSedeId = null) => {
    const dniStr = String(dni);
    const sedeId = resolveSedeForEmpleado(dniStr, preferredSedeId);

    // VALIDACIÓN DE SEGURIDAD: verificar que el empleado pertenece al scope autorizado
    const allowedDnis = new Set();
    authorizedGroups.forEach((g) => g.members.forEach((dni) => allowedDnis.add(String(dni))));
    // Admin bypass
    const rolUsuario = normalizeRole(usuario?.rol);
    const isEmpresaAdmin = isEmpresaAdminRole(rolUsuario);

    if (
      !canSaveRolAssignment({
        isEmpresaAdmin,
        dni: dniStr,
        allowedDnis,
        scopeSedeIds,
        sedeId,
      })
    ) {
      throw new Error(`Usuario no autorizado para modificar empleado o sede: ${dniStr} / ${sedeId}`);
    }

    const payload = { ...data, sedeId };
    await Promise.all([
      setDoc(getRolDocRef(dniStr, sedeId), payload),
      setDoc(getRolLegacyDocRef(dniStr), payload, { merge: true }),
    ]);

    // AUDITORÍA
    try {
      const ultimosAjustes = Array.isArray(data.historialAjustes) ? data.historialAjustes : [];
      const ultimoAjuste =
        ultimosAjustes.length > 0 ? ultimosAjustes[ultimosAjustes.length - 1] : null;

      await logBusinessAction(
        "ACTUALIZACION_ROL_TURNOS",
        "GestionAsistencias",
        {
          dniEditado: dniStr,
          sedeId: sedeId,
          detalleAjuste: ultimoAjuste
            ? ultimoAjuste.origen || ultimoAjuste.tipo || ultimoAjuste.accion || "Configuracion Inicial"
            : "Configuracion Inicial",
          cambiosRealizados: {
            fechaInicio: {
              anterior: ultimoAjuste && ultimoAjuste.before ? ultimoAjuste.before.fechaInicio : null,
              nuevo: data.fechaInicio,
            },
            diasTrabajo: {
              anterior: ultimoAjuste && ultimoAjuste.before ? ultimoAjuste.before.diasTrabajo : null,
              nuevo: data.diasTrabajo,
            },
            diasDescanso: {
              anterior: ultimoAjuste && ultimoAjuste.before ? ultimoAjuste.before.diasDescanso : null,
              nuevo: data.diasDescanso,
            },
          },
        },
        usuario
      );
    } catch (e) {
      console.warn("No se pudo registrar la auditoría en RolTurnos", e);
    }

    return payload;
  };

  // ═══════════════════════════════════════════════════════════════════
  // P0: CARGA INICIAL — Sedes + Grupos + Alcance (mínimo para UI)
  // ═══════════════════════════════════════════════════════════════════
  const cargarContextoInicial = useCallback(async () => {
    if (!rucEmpresa) return;
    setLoading(true);
    try {
      establecerOrigenConsumo("asistencia:rol-turnos");
      const rolUsuario = normalizeRole(usuario?.rol);
      const isEmpresaAdmin = isEmpresaAdminRole(rolUsuario);

      // 1. Cargar sedes para el filtro secundario (~3-5 docs)
      const sedesSnap = await getDocs(
        collection(db, `company_cordenadas/${String(rucEmpresa)}/sedes`)
      );
      const sedesList = sedesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setSedesEmpresa(sedesList);

      // 2. Cargar grupos para el selector principal (~5-10 docs)
      const gruposSnap = await getDocs(
        collection(db, `grupos/${String(rucEmpresa)}/grupos`)
      );
      const allGroups = gruposSnap.docs.map((d) => {
        const data = d.data() || {};
        return {
          id: String(d.id),
          name: data.name || "Grupo sin nombre",
          members: unique(data.members || []),
        };
      });

      if (isEmpresaAdmin) {
        // Admin: todos los grupos + todas las sedes
        setAuthorizedGroups(allGroups);
        setScopeSedeIds(sedesList.map((s) => String(s.id)));
        return;
      }

      // 3. Non-admin: determinar grupos autorizados desde membresía
      const dniUsuario = String(usuario?.dni || "").trim();
      let userGroupIds = [];
      let userSedeIds = [];

      if (dniUsuario) {
        try {
          const membershipRef = doc(
            db,
            `grupos/${String(rucEmpresa)}/memberships`,
            dniUsuario
          );
          const membershipSnap = await getDoc(membershipRef);
          if (membershipSnap.exists()) {
            const mData = membershipSnap.data() || {};
            const singleGroupId = String(mData.groupId || "").trim();
            const multipleGroupIds = Array.isArray(mData.groupIds)
              ? mData.groupIds.map((id) => String(id || "").trim()).filter(Boolean)
              : [];
            userGroupIds = Array.from(
              new Set([singleGroupId, ...multipleGroupIds].filter(Boolean))
            );
            userSedeIds = unique(mData.sedeIds || []);
          }
        } catch (e) {
          console.warn("No se pudo cargar membresía:", e);
        }
      }

      // También grupos donde el DNI del usuario aparece en members
      const groupsWhereUserIsMember = dniUsuario
        ? allGroups.filter((g) => g.members.includes(dniUsuario)).map((g) => g.id)
        : [];

      const allUserGroupIds = Array.from(
        new Set([...userGroupIds, ...groupsWhereUserIsMember])
      );

      // Filtrar a solo grupos autorizados
      const authorized = allUserGroupIds.length > 0
        ? allGroups.filter((g) => allUserGroupIds.includes(g.id))
        : allGroups; // Sin restricción de grupo → todos (edge case)

      setAuthorizedGroups(authorized);

      // 4. Alcance de sede (filtro secundario)
      if (userSedeIds.length > 0) {
        setScopeSedeIds(userSedeIds);
      } else {
        // Sin restricción de sede explícita → todas las sedes
        // (la restricción principal viene del grupo)
        setScopeSedeIds(sedesList.map((s) => String(s.id)));
      }
    } catch (error) {
      console.error("Error al cargar contexto inicial:", error);
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, usuario]);

  useEffect(() => {
    cargarContextoInicial();
  }, [cargarContextoInicial]);

  // ═══════════════════════════════════════════════════════════════════
  // P0: CARGA COMPLETA DEL ROL — Solo por acción explícita del usuario
  // ═══════════════════════════════════════════════════════════════════
  const cargarRolCompleto = useCallback(async () => {
    if (!rucEmpresa || cargandoRol) return;

    // VALIDACIÓN DE SCOPE: verificar que selectedGroupId está autorizado
    if (selectedGroupId !== "ALL" && !authorizedGroups.find((g) => g.id === String(selectedGroupId))) {
      Swal.fire("Acceso denegado", "No tiene acceso al grupo seleccionado.", "error");
      return;
    }
    // VALIDACIÓN DE SCOPE: verificar que selectedSedeId está autorizado
    if (selectedSedeId !== "ALL" && !scopeSedeIds.includes(String(selectedSedeId))) {
      Swal.fire("Acceso denegado", "No tiene acceso a la sede seleccionada.", "error");
      return;
    }

    setCargandoRol(true);
    setLoading(true);
    attendanceCacheRef.current = {}; // Limpiar cache al recargar
    try {
      establecerOrigenConsumo("asistencia:rol-turnos");
      const rolUsuario = normalizeRole(usuario?.rol);
      const isEmpresaAdmin = isEmpresaAdminRole(rolUsuario);

      // 1. Cargar SOLO empleados del scope autorizado (no toda la empresa)
      //    Obtener DNIs de authorizedGroups y consultar por chunks
      let listaEmp = [];
      if (isEmpresaAdmin) {
        // Admin: cargar todos los empleados de la empresa
        const qEmp = query(collection(db, "users"), where("rucEmpresa", "==", rucEmpresa));
        const snapEmp = await getDocs(qEmp);
        snapEmp.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.rol !== "admin" && data.rol !== "superAdmin") {
            listaEmp.push({ id: docSnap.id, ...data });
          }
        });
      } else {
        // Non-admin: cargar SOLO empleados de los grupos autorizados
        const authorizedDnis = new Set();
        if (selectedGroupId !== "ALL") {
          const selectedGroup = authorizedGroups.find((g) => g.id === String(selectedGroupId));
          if (selectedGroup) selectedGroup.members.forEach((dni) => authorizedDnis.add(String(dni)));
        } else {
          authorizedGroups.forEach((g) => g.members.forEach((dni) => authorizedDnis.add(String(dni))));
        }
        const dnisArray = Array.from(authorizedDnis);
        // Firestore 'in' query soporta máximo 10 valores por chunk
        const snaps = await Promise.all(
          chunkValues(dnisArray, 10).map((chunk) =>
            getDocs(query(collection(db, "users"), where("dni", "in", chunk)))
          )
        );
        snaps.forEach((snap) => {
          snap.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.rol !== "admin" && data.rol !== "superAdmin") {
              listaEmp.push({ id: docSnap.id, ...data });
            }
          });
        });
      }

      // 2. Alcance por grupo — usando authorizedGroups pre-cargados en cargarContextoInicial
      let dnisPermitidosGrupo = null;
      if (!isEmpresaAdmin) {
        if (selectedGroupId !== "ALL") {
          const selectedGroup = authorizedGroups.find((g) => g.id === String(selectedGroupId));
          dnisPermitidosGrupo = selectedGroup
            ? new Set(selectedGroup.members.map(String))
            : new Set();
        } else {
          const allMembers = new Set();
          authorizedGroups.forEach((g) =>
            g.members.forEach((dni) => allMembers.add(String(dni)))
          );
          dnisPermitidosGrupo = allMembers;
        }
      }

      // 3. Cargar membresías SOLO de empleados autorizados (no toda la empresa)
      //    Document ID = DNI, así que podemos cargar por DNI individuales
      const authorizedDnisForMemberships = Array.from(dnisPermitidosGrupo || new Set());
      const memberships = {};

      if (isEmpresaAdmin) {
        // Admin: cargar todas las memberships (necesita sede mapping completo)
        const membershipsSnap = await getDocs(
          collection(db, `grupos/${String(rucEmpresa)}/memberships`)
        );
        membershipsSnap.forEach((d) => {
          const data = d.data() || {};
          const dni = String(data.dni || d.id || "");
          memberships[dni] = {
            ...data,
            dni,
            sedeIds: unique(data.sedeIds || []),
          };
        });
      } else {
        // Non-admin: cargar SOLO memberships de DNIs autorizados
        const membershipPromises = authorizedDnisForMemberships.map((dni) =>
          getDoc(doc(db, `grupos/${String(rucEmpresa)}/memberships`, String(dni)))
            .then((snap) => {
              if (snap.exists()) {
                const data = snap.data() || {};
                memberships[String(dni)] = {
                  ...data,
                  dni: String(dni),
                  sedeIds: unique(data.sedeIds || []),
                };
              }
              return null;
            })
            .catch(() => null) // Silenciar errores de DNIs sin membership
        );
        await Promise.all(membershipPromises);
      }
      setMembershipsMap(memberships);

      // 4. Sedes — reutilizar si ya cargadas por cargarContextoInicial
      //    NO recalcular scopeSedeIds — ya viene de cargarContextoInicial
      let sedesList = sedesEmpresa;
      if (sedesList.length === 0) {
        const sedesSnap = await getDocs(
          collection(db, `company_cordenadas/${String(rucEmpresa)}/sedes`)
        );
        sedesList = sedesSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));
        setSedesEmpresa(sedesList);
      }

      // 5. Empleados — ya filtrados por DNI del scope autorizado en paso 1
      //    Aplicar group filter adicional si es necesario (para ALL groups)
      const empleadosConAlcanceGrupo = dnisPermitidosGrupo
        ? listaEmp.filter((emp) => dnisPermitidosGrupo.has(String(emp.dni || "")))
        : listaEmp;

      empleadosConAlcanceGrupo.sort((a, b) =>
        (a.apellidos || "").localeCompare(b.apellidos || "")
      );
      setEmpleados(empleadosConAlcanceGrupo);

      // 7. Cargar Roles por sede — usar sedes derivadas de los empleados cargados
      const mapa = {};
      // Derivar sedes de los empleados autorizados (no depender de scopeSedeIds para roles)
      const empleadosSedes = unique(
        empleadosConAlcanceGrupo.map((emp) => {
          const ms = unique(memberships[String(emp.dni)]?.sedeIds || []);
          return ms.length > 0 ? ms[0] : "general";
        })
      );
      const sedesObjetivo = unique(
        isEmpresaAdmin ? sedesList.map((s) => String(s.id)) : empleadosSedes
      );

      if (sedesObjetivo.length > 0) {
        await Promise.all(
          sedesObjetivo.map(async (sedeId) => {
            const snap = await getDocs(
              collection(
                db,
                "pl_rol_turnos",
                String(rucEmpresa),
                "sedes",
                String(sedeId),
                "rol"
              )
            );
            snap.forEach((docSnap) => {
              const dni = String(docSnap.id);
              if (!mapa[dni]) {
                mapa[dni] = {
                  ...docSnap.data(),
                  sedeId: String(docSnap.data()?.sedeId || sedeId),
                };
              }
            });
          })
        );
      }

      // Fallback legacy por DNI faltante
      // LIMITACIÓN: La estructura legacy no permite filtrar por DNI en Firestore.
      // Se descargan todos los roles y se filtran por scope autorizado.
      const legacySnap = await getDocs(
        collection(db, "pl_rol_turnos", String(rucEmpresa), "rol")
      );
      legacySnap.forEach((docSnap) => {
        const dni = String(docSnap.id);
        // Solo guardar si el DNI está en el scope autorizado
        if (!mapa[dni] && (!dnisPermitidosGrupo || dnisPermitidosGrupo.has(dni))) {
          mapa[dni] = {
            ...docSnap.data(),
            sedeId: "general",
            source: "legacy",
          };
        }
      });

      // Respaldo temporal extra: colección asistencia_roles (muy legacy)
      // Solo se ejecuta si no se encontró ningún rol en las estructuras principales
      if (Object.keys(mapa).length === 0) {
        const qRolesLegacy = query(
          collection(db, "asistencia_roles"),
          where("rucEmpresa", "==", rucEmpresa)
        );
        const snapLegacy = await getDocs(qRolesLegacy);
        snapLegacy.forEach((docSnap) => {
          const dni = String(docSnap.id);
          if (!dnisPermitidosGrupo || dnisPermitidosGrupo.has(dni)) {
            mapa[dni] = {
              ...docSnap.data(),
              sedeId: "general",
              source: "asistencia_roles",
            };
          }
        });
      }

      const mapaFiltrado = dnisPermitidosGrupo
        ? Object.fromEntries(
            Object.entries(mapa).filter(([dni]) => dnisPermitidosGrupo.has(String(dni)))
          )
        : mapa;
      setRolesMap(mapaFiltrado);

      // 8. Cargar regímenes base
      let regDocs = [];
      const qRegRuc = query(
        collection(db, "pl_regimen_trabajo"),
        where("ruc", "==", rucEmpresa)
      );
      const snapRegRuc = await getDocs(qRegRuc);
      regDocs = snapRegRuc.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (regDocs.length === 0) {
        const qRegRucEmpresa = query(
          collection(db, "pl_regimen_trabajo"),
          where("rucEmpresa", "==", rucEmpresa)
        );
        const snapRegRucEmpresa = await getDocs(qRegRucEmpresa);
        regDocs = snapRegRucEmpresa.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      if (regDocs.length > 0) {
        const mapped = regDocs
          .map((r) => ({
            id: String(r.id),
            nombre: String(r.nombre || r.descripcion || r.id || "Régimen"),
            diasTrabajo: Number(r.dias_trabajo ?? 0),
            diasDescanso: Number(r.dias_descanso ?? 0),
            raw: r,
          }))
          .filter((r) => r.diasTrabajo > 0 && r.diasDescanso > 0)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));

        setRegimenesBase([
          { id: "PERSONALIZADO", nombre: "Personalizado", diasTrabajo: null, diasDescanso: null },
          ...mapped,
        ]);
      } else {
        setRegimenesBase(REGIMENES_FALLBACK);
      }

      // P0: Marcar como cargado — esto dispara la carga de marcaciones vía useEffect
      setRolCargado(true);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    } finally {
      setLoading(false);
      setCargandoRol(false);
    }
  }, [rucEmpresa, usuario, sedesEmpresa, cargandoRol, authorizedGroups, selectedGroupId, selectedSedeId, scopeSedeIds]);

  // ═══════════════════════════════════════════════════════════════════
  // P2: CARGA DE MARCACIONES — Con cache y solo cuando rolCargado
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    const cargarMarcacionesMes = async () => {
      // P0: No cargar si el rol no ha sido solicitado explícitamente
      if (!rolCargado || !rucEmpresa || empleados.length === 0) {
        setAttendanceStatusMap({});
        return;
      }

      const monthKey = format(mesActual, "yyyy-MM");
      // P2: Usar cache — si ya tenemos este mes, no volver a consultar Firestore
      const cached = attendanceCacheRef.current[monthKey];
      if (cached) {
        setAttendanceStatusMap(cached);
        return;
      }

      try {
        const fechaInicio = format(startOfMonth(mesActual), "yyyy-MM-dd");
        const fechaFin = format(endOfMonth(mesActual), "yyyy-MM-dd");
        const dnis = unique(empleados.map((e) => e?.dni));
        const dnisSet = new Set(dnis);
        const records = [];

        if (dnis.length > 0) {
          try {
            const snaps = await Promise.all(
              chunkValues(dnis, 10).map((chunk) =>
                getDocs(
                  query(
                    collectionGroup(db, "registros"),
                    where("ruc", "==", rucEmpresa),
                    where("dni", "in", chunk),
                    where("fechaJornada", ">=", fechaInicio),
                    where("fechaJornada", "<=", fechaFin)
                  )
                )
              )
            );
            snaps.forEach((snap) =>
              snap.forEach((docSnap) => records.push(docSnap.data()))
            );
          } catch (scopeQueryError) {
            console.error(
              "Error en consulta de marcaciones (índice compuesto requerido):",
              scopeQueryError?.message
            );
            // No realizar fallback masivo — mostrar error controlado
            // El índice compuesto (ruc, dni, fechaJornada) es necesario
            setAttendanceStatusMap({});
            return;
          }
        }

        const statusMap = {};
        records.forEach((record) => {
          const dni = String(record?.dni || "");
          if (!dnisSet.has(dni)) return;
          const fecha = String(record?.fechaJornada || "");
          if (!fecha) return;
          const tipo = String(record?.tipo || "").toLowerCase();
          const key = `${dni}__${fecha}`;
          if (!statusMap[key]) statusMap[key] = { ingreso: false, salida: false };
          if (tipo === "ingreso") statusMap[key].ingreso = true;
          if (tipo === "salida") statusMap[key].salida = true;
        });

        // P2: Guardar en cache y actualizar estado
        attendanceCacheRef.current[monthKey] = statusMap;
        setAttendanceStatusMap(statusMap);
      } catch (error) {
        console.error("Error al cargar marcaciones del mes:", error);
        setAttendanceStatusMap({});
      }
    };

    cargarMarcacionesMes();
  }, [rolCargado, rucEmpresa, mesActual, empleados]);

  // Auto-selección + validación de sede
  useEffect(() => {
    // Auto-selección: si solo 1 grupo autorizado, seleccionarlo
    if (authorizedGroups.length === 1 && selectedGroupId === "ALL") {
      setSelectedGroupId(String(authorizedGroups[0].id));
    }
    // Auto-selección: si solo 1 sede autorizada, seleccionarla
    if (scopeSedeIds.length === 1 && selectedSedeId === "ALL") {
      setSelectedSedeId(String(scopeSedeIds[0]));
      return;
    }
    // Validar sede seleccionada
    if (selectedSedeId === "ALL") return;
    const allowed = scopeSedeIds.includes(String(selectedSedeId));
    if (!allowed) setSelectedSedeId("ALL");
  }, [selectedSedeId, scopeSedeIds, selectedGroupId, authorizedGroups, usuario]);

  // ═══════════════════════════════════════════════════════════════════
  // NAVEGACIÓN DE MES
  // ═══════════════════════════════════════════════════════════════════
  const cambiarMes = (offset) => {
    setMesActual((prev) => {
      const nuevo = new Date(prev);
      nuevo.setMonth(nuevo.getMonth() + offset);
      return nuevo;
    });
  };

  // ═══════════════════════════════════════════════════════════════════
  // CÁLCULO DE TURNO POR CELDA (usado por modal y exportación)
  // ═══════════════════════════════════════════════════════════════════
  const getShiftInfo = (dniInput, date) => {
    const config = rolesMap[String(dniInput)];
    if (!config || !config.fechaInicio) return { label: "-", estado: "Ninguno" };

    const dateKey = format(date, "yyyy-MM-dd");

    if (config.fechaCese && String(dateKey) >= String(config.fechaCese)) {
      const metaCese = getExceptionMeta("C");
      return { label: metaCese.code, estado: metaCese.estado, excepcion: true, cese: true };
    }

    if (config.excepciones && config.excepciones[dateKey]) {
      const type = config.excepciones[dateKey];
      const meta = getExceptionMeta(type);
      const label = `${meta.code}*`;
      const estado = meta.estado;
      return { label, estado, excepcion: true };
    }

    const startDate = parseISO(config.fechaInicio);
    if (isNaN(startDate.getTime())) return { label: "Err", estado: "Ninguno" };

    const diffDays = differenceInCalendarDays(date, startDate);
    const diasTrabajo = Number(config.diasTrabajo) || 24;
    const diasDescanso = Number(config.diasDescanso) || 14;
    const cycleLength = diasTrabajo + diasDescanso;

    if (cycleLength <= 0) return { label: "-", estado: "Ninguno" };

    let positionInCycle = diffDays % cycleLength;
    if (positionInCycle < 0) {
      positionInCycle = (cycleLength + positionInCycle) % cycleLength;
    }

    if (positionInCycle < diasTrabajo) {
      return { label: `T${positionInCycle + 1}`, estado: "Trabajo" };
    } else {
      return { label: `D${positionInCycle - diasTrabajo + 1}`, estado: "Descanso" };
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // CONFIGURACIÓN INDIVIDUAL
  // ═══════════════════════════════════════════════════════════════════
  const abrirConfiguracion = (emp) => {
    setEmpModal(emp);
    const config = rolesMap[emp.dni] || {
      fechaInicio: format(new Date(), "yyyy-MM-dd"),
      diasTrabajo: 24,
      diasDescanso: 14,
      regimenId: "PERSONALIZADO",
      regimenNombre: "Personalizado",
    };

    const diasTrabajoActual = Number(config.diasTrabajo || 24);
    const diasDescansoActual = Number(config.diasDescanso || 14);
    const regimenMatch = regimenesBase.find(
      (r) =>
        r.id !== "PERSONALIZADO" &&
        r.diasTrabajo === diasTrabajoActual &&
        r.diasDescanso === diasDescansoActual
    );
    const regimenId = config.regimenId || regimenMatch?.id || "PERSONALIZADO";
    const regimenNombre =
      config.regimenNombre ||
      regimenesBase.find((r) => r.id === regimenId)?.nombre ||
      "Personalizado";

    setConfigTemp({
      fechaInicio: config.fechaInicio || format(new Date(), "yyyy-MM-dd"),
      diasTrabajo: diasTrabajoActual,
      diasDescanso: diasDescansoActual,
      regimenId,
      regimenNombre,
    });
    setShowModal(true);
  };

  const guardarConfiguracion = async () => {
    if (!empModal) return;
    setGuardando(true);
    try {
      if (!configTemp.fechaInicio) {
        Swal.fire("Validación", "La fecha de inicio es obligatoria.", "warning");
        return;
      }
      if (
        (Number(configTemp.diasTrabajo) || 0) <= 0 ||
        (Number(configTemp.diasDescanso) || 0) <= 0
      ) {
        Swal.fire(
          "Validación",
          "Días de trabajo y descanso deben ser mayores a 0.",
          "warning"
        );
        return;
      }

      const data = {
        dni: empModal.dni,
        nombre: `${empModal.name || ""} ${empModal.apellidos || ""}`,
        rucEmpresa: rucEmpresa,
        ...configTemp,
        regimenMeta:
          configTemp.regimenId && configTemp.regimenId !== "PERSONALIZADO"
            ? regimenesBase.find((r) => r.id === configTemp.regimenId)?.raw || null
            : null,
        ultimaActualizacion: new Date().toISOString(),
      };
      const dniStr = String(empModal.dni);
      const payload = await persistRolData(dniStr, data);

      // Actualizar mapa localmente — P1: sin refreshKey
      setRolesMap((prev) => ({ ...prev, [dniStr]: payload }));

      setShowModal(false);
      Swal.fire("Éxito", "Configuración guardada", "success");
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire("Error", "No se pudo guardar la configuración", "error");
    } finally {
      setGuardando(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // AJUSTE DE CELDA
  // ═══════════════════════════════════════════════════════════════════
  const abrirAjuste = (emp, dia) => {
    const info = getShiftInfo(emp.dni, dia);
    setAdjData({ emp, dia, info });

    const type = info.estado === "Descanso" ? "Descanso" : "Trabajo";
    const numMatch = info.label.match(/\d+/);
    const num = numMatch ? parseInt(numMatch[0]) : 1;
    setAdjConfig({ type, num });
    setAdjFlex({ dias: 1, compensacion: "DESCANSO", motivo: "" });
    setAdjShiftDays(1);
    setAdjContext({ factor: "CLIMA", detalle: "" });

    setShowAdjModal(true);
  };

  const aplicarRangoExcepcion = (excepcionesBase, fechaInicio, dias, estado) => {
    const out = { ...(excepcionesBase || {}) };
    for (let i = 0; i < dias; i += 1) {
      const key = format(addDays(fechaInicio, i), "yyyy-MM-dd");
      out[key] = estado;
    }
    return out;
  };

  const aplicarAjusteFlexible = async (accion) => {
    if (!adjData) return;
    const { emp, dia } = adjData;
    const dniStr = String(emp.dni);
    const config = rolesMap[dniStr] || {
      dni: dniStr,
      nombre: `${emp.name || ""} ${emp.apellidos || ""}`,
      rucEmpresa,
      diasTrabajo: 24,
      diasDescanso: 14,
      fechaInicio: format(new Date(), "yyyy-MM-dd"),
      excepciones: {},
    };

    const dias = Math.max(1, Number(adjFlex.dias) || 1);
    const comp = adjFlex.compensacion || "DESCANSO";
    const bancoActual = {
      pendienteDescanso: Number(config?.bancoCompensacion?.pendienteDescanso || 0),
      pendientePago: Number(config?.bancoCompensacion?.pendientePago || 0),
      movimientos: Array.isArray(config?.bancoCompensacion?.movimientos)
        ? config.bancoCompensacion.movimientos
        : [],
    };

    let nuevasExcepciones = { ...(config.excepciones || {}) };
    let pendienteDescanso = bancoActual.pendienteDescanso;
    let pendientePago = bancoActual.pendientePago;
    let estadoExcepcion = "T";
    let detalle = "";

    if (accion === "AUMENTAR_TRABAJO") {
      estadoExcepcion = "T";
      nuevasExcepciones = aplicarRangoExcepcion(nuevasExcepciones, dia, dias, estadoExcepcion);
      if (comp === "DESCANSO") pendienteDescanso += dias;
      if (comp === "PAGO") pendientePago += dias;
      detalle = `Se agregaron ${dias} día(s) de trabajo`;
    }
    if (accion === "REDUCIR_TRABAJO") {
      estadoExcepcion = "D";
      nuevasExcepciones = aplicarRangoExcepcion(nuevasExcepciones, dia, dias, estadoExcepcion);
      const consumidos = Math.min(dias, pendienteDescanso);
      pendienteDescanso -= consumidos;
      detalle = `Se redujeron ${dias} día(s) de trabajo`;
    }
    if (accion === "AUMENTAR_DESCANSO") {
      estadoExcepcion = "D";
      nuevasExcepciones = aplicarRangoExcepcion(nuevasExcepciones, dia, dias, estadoExcepcion);
      const consumidos = Math.min(dias, pendienteDescanso);
      pendienteDescanso -= consumidos;
      detalle = `Se agregaron ${dias} día(s) de descanso`;
    }
    if (accion === "REDUCIR_DESCANSO") {
      estadoExcepcion = "T";
      nuevasExcepciones = aplicarRangoExcepcion(nuevasExcepciones, dia, dias, estadoExcepcion);
      if (comp === "DESCANSO") pendienteDescanso += dias;
      if (comp === "PAGO") pendientePago += dias;
      detalle = `Se redujeron ${dias} día(s) de descanso`;
    }

    const data = {
      ...config,
      excepciones: nuevasExcepciones,
      bancoCompensacion: {
        pendienteDescanso,
        pendientePago,
        movimientos: [
          ...bancoActual.movimientos,
          {
            fecha: new Date().toISOString(),
            diaBase: format(dia, "yyyy-MM-dd"),
            accion,
            dias,
            compensacion: comp,
            detalle,
            factor: adjContext.factor || "OTRO",
            motivo: adjFlex.motivo || adjContext.detalle || "",
          },
        ].slice(-200),
      },
      historialAjustes: [
        ...(Array.isArray(config.historialAjustes) ? config.historialAjustes : []),
        {
          fecha: new Date().toISOString(),
          origen: "AJUSTE_FLEXIBLE",
          accion,
          dias,
          compensacion: comp,
          factor: adjContext.factor || "OTRO",
          detalle: adjContext.detalle || adjFlex.motivo || "",
          before: {
            fechaInicio: config.fechaInicio,
            diasTrabajo: config.diasTrabajo,
            diasDescanso: config.diasDescanso,
            excepciones: deepClone(config.excepciones || {}),
            bancoCompensacion: deepClone(config.bancoCompensacion || {}),
          },
        },
      ].slice(-300),
      ultimaActualizacion: new Date().toISOString(),
    };

    setGuardando(true);
    try {
      const payload = await persistRolData(dniStr, data);
      // P1: Actualizar directamente sin refreshKey
      setRolesMap((prev) => ({ ...prev, [dniStr]: payload }));
      Swal.fire(
        "Éxito",
        `${detalle}. Banco descanso: ${pendienteDescanso} / pago: ${pendientePago}`,
        "success"
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo guardar el ajuste flexible.", "error");
    } finally {
      setGuardando(false);
    }
  };

  const ajustarCalendario = async (tipo) => {
    if (!adjData) return;
    const { emp, dia } = adjData;
    const dniStr = String(emp.dni);
    const config = rolesMap[dniStr] || {
      dni: dniStr,
      nombre: (emp.name || "") + " " + (emp.apellidos || ""),
      rucEmpresa: rucEmpresa,
      diasTrabajo: 24,
      diasDescanso: 14,
      fechaInicio: format(new Date(), "yyyy-MM-dd"),
    };

    let nuevaFechaInicio;
    const diasTrabajo = Number(config.diasTrabajo) || 24;

    const dateKey = format(dia, "yyyy-MM-dd");

    const shiftDays = Math.max(1, Number(adjShiftDays) || 1);
    if (tipo === "T1") {
      nuevaFechaInicio = format(dia, "yyyy-MM-dd");
    } else if (tipo === "D1") {
      nuevaFechaInicio = format(subDays(dia, diasTrabajo), "yyyy-MM-dd");
    } else if (tipo === "PUSH") {
      const currentStart = new Date(config.fechaInicio + "T00:00:00");
      nuevaFechaInicio = format(addDays(currentStart, shiftDays), "yyyy-MM-dd");
    } else if (tipo === "PULL") {
      const currentStart = new Date(config.fechaInicio + "T00:00:00");
      nuevaFechaInicio = format(subDays(currentStart, shiftDays), "yyyy-MM-dd");
    } else if (tipo.startsWith("EXC_")) {
      const nuevasExcepciones = { ...(config.excepciones || {}) };
      const nuevosComentariosExcepcion = { ...(config.excepcionesComentarios || {}) };
      const excType = tipo.split("_")[1];
      let nuevaFechaCese = config.fechaCese || null;
      let detalleAjuste = String(adjContext.detalle || "").trim();

      if (excType === "LO") {
        const comentarioPrevio = String(
          nuevosComentariosExcepcion[dateKey] || detalleAjuste || ""
        ).trim();
        const promptComentario = await Swal.fire({
          title: "Comentario requerido",
          text: "Registra el motivo de Licencia Otros (LO)",
          input: "textarea",
          inputLabel: "Comentario",
          inputPlaceholder: "Ejemplo: Licencia por muerte de padre",
          inputValue: comentarioPrevio,
          inputAttributes: {
            maxlength: "250",
          },
          showCancelButton: true,
          confirmButtonText: "Guardar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#2563eb",
          inputValidator: (value) => {
            if (!String(value || "").trim()) return "Debes ingresar un comentario.";
            return undefined;
          },
        });

        if (!promptComentario.isConfirmed) return;

        detalleAjuste = String(promptComentario.value || "").trim();
        setAdjContext((prev) => ({ ...prev, detalle: detalleAjuste }));
      }

      if (excType === "CLEAR") {
        delete nuevasExcepciones[dateKey];
        delete nuevosComentariosExcepcion[dateKey];
      } else if (excType === "C") {
        nuevaFechaCese = dateKey;
      } else {
        nuevasExcepciones[dateKey] = excType;
        if (excType === "LO") nuevosComentariosExcepcion[dateKey] = detalleAjuste;
        else delete nuevosComentariosExcepcion[dateKey];
      }

      if (excType === "CLEAR" && config.fechaCese && String(config.fechaCese) === String(dateKey)) {
        nuevaFechaCese = null;
      }

      setGuardando(true);
      try {
        const data = {
          ...config,
          excepciones: nuevasExcepciones,
          excepcionesComentarios: nuevosComentariosExcepcion,
          fechaCese: nuevaFechaCese,
          ultimaActualizacion: new Date().toISOString(),
        };
        data.historialAjustes = [
          ...(Array.isArray(config.historialAjustes) ? config.historialAjustes : []),
          {
            fecha: new Date().toISOString(),
            origen: "EXCEPCION_DIA",
            tipo,
            factor: adjContext.factor || "OTRO",
            detalle: detalleAjuste,
            before: {
              fechaInicio: config.fechaInicio,
              diasTrabajo: config.diasTrabajo,
              diasDescanso: config.diasDescanso,
              excepciones: deepClone(config.excepciones || {}),
              excepcionesComentarios: deepClone(config.excepcionesComentarios || {}),
              bancoCompensacion: deepClone(config.bancoCompensacion || {}),
            },
          },
        ].slice(-300);
        const payload = await persistRolData(dniStr, data);
        // P1: sin refreshKey
        setRolesMap((prev) => ({ ...prev, [dniStr]: payload }));
        setShowAdjModal(false);
        Swal.fire("Éxito", "Estado actualizado para este día", "success");
        return;
      } catch (e) {
        console.error("Error al guardar excepción LP/LO:", e);
        const detalle = e?.message || e?.code || String(e);
        Swal.fire("Error", `No se pudo guardar: ${detalle}`, "error");
        return;
      } finally {
        setGuardando(false);
      }
    }

    let finalDiasTrabajo = diasTrabajo;
    let finalDiasDescanso = Number(config.diasDescanso) || 14;

    if (tipo === "CUSTOM") {
      if ((Number(adjConfig.num) || 0) <= 0) {
        Swal.fire("Validación", "El número de día debe ser mayor a 0.", "warning");
        return;
      }
      if (adjConfig.type === "Trabajo" && adjConfig.num > finalDiasTrabajo) {
        finalDiasTrabajo = adjConfig.num;
      }
      if (adjConfig.type === "Descanso" && adjConfig.num > finalDiasDescanso) {
        finalDiasDescanso = adjConfig.num;
      }

      const targetPos =
        adjConfig.type === "Trabajo"
          ? adjConfig.num - 1
          : finalDiasTrabajo + adjConfig.num - 1;
      nuevaFechaInicio = format(subDays(dia, targetPos), "yyyy-MM-dd");
    }

    setGuardando(true);
    try {
      const data = {
        ...config,
        fechaInicio: nuevaFechaInicio,
        diasTrabajo: finalDiasTrabajo,
        diasDescanso: finalDiasDescanso,
        historialAjustes: [
          ...(Array.isArray(config.historialAjustes) ? config.historialAjustes : []),
          {
            fecha: new Date().toISOString(),
            tipo,
            dias: tipo === "PUSH" || tipo === "PULL" ? shiftDays : 0,
            factor: adjContext.factor || "OTRO",
            detalle: adjContext.detalle || "",
            before: {
              fechaInicio: config.fechaInicio,
              diasTrabajo: config.diasTrabajo,
              diasDescanso: config.diasDescanso,
              excepciones: deepClone(config.excepciones || {}),
              bancoCompensacion: deepClone(config.bancoCompensacion || {}),
            },
          },
        ].slice(-300),
        ultimaActualizacion: new Date().toISOString(),
      };
      const payload = await persistRolData(dniStr, data);
      // P1: sin refreshKey
      setRolesMap((prev) => ({ ...prev, [dniStr]: payload }));
      setShowAdjModal(false);
      const msgShift =
        tipo === "PUSH" || tipo === "PULL"
          ? ` | Desplazamiento: ${shiftDays} día(s)`
          : "";
      Swal.fire(
        "Éxito",
        `Calendario ajustado. Nueva fecha base: ${nuevaFechaInicio}${msgShift}`,
        "success"
      );
    } catch (error) {
      console.error("Error al ajustar:", error);
      Swal.fire("Error", "No se pudo realizar el ajuste", "error");
    } finally {
      setGuardando(false);
    }
  };

  const deshacerUltimoAjuste = async () => {
    if (!adjData) return;
    const dniStr = String(adjData.emp?.dni || "");
    const config = rolesMap[dniStr];
    if (!config) return;

    const historial = Array.isArray(config.historialAjustes)
      ? config.historialAjustes
      : [];
    if (historial.length === 0) {
      Swal.fire("Sin cambios", "No hay ajustes para deshacer.", "info");
      return;
    }

    const ultimo = historial[historial.length - 1];
    if (!ultimo.before) {
      Swal.fire(
        "No reversible",
        "El último ajuste no tiene snapshot para deshacer.",
        "warning"
      );
      return;
    }

    const confirmado = await Swal.fire({
      title: "¿Deshacer último ajuste?",
      text: `Se revertirá: ${ultimo.origen || ultimo.tipo || ultimo.accion || "ajuste"}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, deshacer",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#2563eb",
    });
    if (!confirmado.isConfirmed) return;

    setGuardando(true);
    try {
      const data = {
        ...config,
        fechaInicio: ultimo.before.fechaInicio || config.fechaInicio,
        diasTrabajo: Number(ultimo.before.diasTrabajo || config.diasTrabajo || 24),
        diasDescanso: Number(ultimo.before.diasDescanso || config.diasDescanso || 14),
        excepciones: deepClone(ultimo.before.excepciones || {}),
        excepcionesComentarios: deepClone(
          ultimo.before.excepcionesComentarios || config.excepcionesComentarios || {}
        ),
        bancoCompensacion: deepClone(
          ultimo.before.bancoCompensacion || config.bancoCompensacion || {}
        ),
        historialAjustes: historial.slice(0, -1),
        ultimaActualizacion: new Date().toISOString(),
      };
      const payload = await persistRolData(dniStr, data);
      // P1: sin refreshKey
      setRolesMap((prev) => ({ ...prev, [dniStr]: payload }));
      Swal.fire("Éxito", "Se deshizo el último ajuste.", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo deshacer el último ajuste.", "error");
    } finally {
      setGuardando(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // MEMOS DERIVADOS
  // ═══════════════════════════════════════════════════════════════════
  const diasMes = useMemo(() => {
    const start = startOfMonth(mesActual);
    const end = endOfMonth(mesActual);
    return eachDayOfInterval({ start, end });
  }, [mesActual]);

  const empleadosFiltrados = useMemo(() => {
    const term = String(busqueda || "").toLowerCase();
    // Grupo como filtro primario de autorización
    const selectedGroup =
      selectedGroupId === "ALL"
        ? null
        : authorizedGroups.find((g) => g.id === String(selectedGroupId));
    const groupDnis = selectedGroup
      ? new Set(selectedGroup.members.map(String))
      : null; // null = sin filtro de grupo (mostrar todos los autorizados)

    return empleados.filter((e) => {
      const dni = String(e.dni || "");
      const fullNombre = `${e.name || ""} ${e.apellidos || ""}`.toLowerCase();

      // Filtro GRUPO (primario)
      if (groupDnis && !groupDnis.has(dni)) return false;

      // Filtro SEDE (secundario)
      const memberSedes = unique(membershipsMap[dni]?.sedeIds || []);
      const matchSede =
        selectedSedeId === "ALL" ? true : memberSedes.includes(String(selectedSedeId));

      // Filtro búsqueda
      const matchTerm = fullNombre.includes(term) || dni.includes(term);

      return matchTerm && matchSede;
    });
  }, [empleados, busqueda, membershipsMap, selectedSedeId, selectedGroupId, authorizedGroups]);

  const regimenSeleccionado = useMemo(() => {
    const reg = regimenesBase.find(
      (r) => r.id === (configTemp.regimenId || "PERSONALIZADO")
    );
    return reg || null;
  }, [regimenesBase, configTemp.regimenId]);

  // ═══════════════════════════════════════════════════════════════════
  // P2: GRID DE TURNOS PRE-CALCULADO
  // ═══════════════════════════════════════════════════════════════════
  const shiftGrid = useMemo(() => {
    if (!rolCargado || empleadosFiltrados.length === 0) return {};
    const grid = {};
    empleadosFiltrados.forEach((emp) => {
      const dni = String(emp.dni);
      const empCells = [];
      let totalTrabajados = 0;
      let totalDescansados = 0;
      diasMes.forEach((dia) => {
        const info = getShiftInfo(dni, dia);
        const dateKey = format(dia, "yyyy-MM-dd");
        const baseLabel = String(info.label || "")
          .replace("*", "")
          .trim()
          .toUpperCase();
        const isTrabajo = baseLabel.startsWith("T") || baseLabel.startsWith("N");
        const isDescanso = baseLabel.startsWith("D");
        const isWork = info.estado === "Trabajo";
        const isExc = info.excepcion;
        const comentarioExcepcion = String(
          rolesMap[dni]?.excepcionesComentarios?.[dateKey] || ""
        ).trim();
        const isPapeletaStyled =
          String(rolesMap[dni]?.papeletaStyleByDate?.[dateKey] || "") ===
          "PAPELETA_APROBADA";
        const markStatus = attendanceStatusMap[`${dni}__${dateKey}`];
        const hasIngreso = Boolean(markStatus?.ingreso);
        const hasSalida = Boolean(markStatus?.salida);
        const hasCompleteMark = hasIngreso && hasSalida;
        const hasPartialMark = (hasIngreso || hasSalida) && !hasCompleteMark;

        if (isTrabajo) totalTrabajados += 1;
        if (isDescanso) totalDescansados += 1;

        // Precalcular colores (antes en cada render)
        let bgColor = "white";
        let textColor = "#94a3b8";

        if (isExc) {
          const code = String(info.label || "")
            .replace("*", "")
            .trim();
          const meta = getExceptionMeta(code);
          bgColor = meta.bgColor;
          textColor = meta.textColor;
        } else {
          if (isWork) {
            bgColor = "white";
            textColor = "#2c7a2c";
          }
          if (isTrabajo && hasCompleteMark) bgColor = "#d1fae5";
          else if (isTrabajo && hasPartialMark) bgColor = "#fef9c3";
          else if (isToday(dia)) bgColor = "#fefce8";
          else if (isWeekend(dia)) bgColor = "#f8fafc";
        }
        if (isPapeletaStyled) {
          bgColor = "#1d4ed8";
          textColor = "#ffffff";
        }

        const hoverTitle =
          baseLabel === "LO" && comentarioExcepcion
            ? `LO: ${comentarioExcepcion}`
            : "";

        empCells.push({
          dia,
          info,
          baseLabel,
          isWork,
          isExc,
          hoverTitle,
          bgColor,
          textColor,
        });
      });
      grid[dni] = {
        cells: empCells,
        trabajados: totalTrabajados,
        descansados: totalDescansados,
      };
    });
    return grid;
  }, [rolCargado, empleadosFiltrados, diasMes, rolesMap, attendanceStatusMap]);

  // ═══════════════════════════════════════════════════════════════════
  // EXPORTAR EXCEL
  // ═══════════════════════════════════════════════════════════════════
  const exportarExcel = () => {
    try {
      if (!empleadosFiltrados.length) {
        Swal.fire(
          "Sin datos",
          "No hay colaboradores para exportar con el filtro actual.",
          "info"
        );
        return;
      }

      const rows = empleadosFiltrados.map((emp, idx) => {
        const row = {
          "#": idx + 1,
          DNI: String(emp.dni || ""),
          Colaborador: `${emp.apellidos || ""} ${emp.name || ""}`.trim(),
        };
        let totalTrabajados = 0;
        let totalDescansados = 0;

        diasMes.forEach((dia) => {
          const key = format(dia, "dd/MM");
          const info = getShiftInfo(String(emp.dni), dia);
          const baseLabel = String(info.label || "")
            .replace("*", "")
            .trim()
            .toUpperCase();
          if (baseLabel.startsWith("T") || baseLabel.startsWith("N")) totalTrabajados += 1;
          if (baseLabel.startsWith("D")) totalDescansados += 1;
          row[key] = info.label || "-";
        });
        row.Trabajados = totalTrabajados;
        row.Descansados = totalDescansados;

        return row;
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RolTurnos");
      const nombre = `rol_turnos_${format(mesActual, "yyyy_MM")}.xlsx`;
      XLSX.writeFile(wb, nombre);
      Swal.fire("Éxito", `Archivo exportado: ${nombre}`, "success");
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      Swal.fire("Error", "No se pudo exportar el rol de turnos.", "error");
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════
  return (
    <Container
      fluid
      className="py-4 px-4 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        minHeight: "100vh",
      }}
    >
      <Card
        className="shadow-lg border-0 mb-4"
        style={{ borderRadius: "15px", overflow: "hidden" }}
      >
        {/* Header */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f59e0b",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaCalendarAlt className="me-3" />
              Rol de Turnos General
            </h2>
            <p className="mb-0 opacity-75">
              Planificación y control de horarios, turnos y descansos
            </p>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {/* Acciones Rápidas */}
          <Card
            className="border-0 mb-4 shadow-sm"
            style={{
              background: "linear-gradient(135deg, #fffcea, #fff9db)",
              borderRadius: 14,
            }}
          >
            <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap align-items-center">
              <div className="d-flex align-items-center bg-white px-3 py-2 rounded-pill shadow-sm">
                <Button
                  variant="link"
                  className="text-dark p-1"
                  onClick={() => cambiarMes(-1)}
                >
                  <FaChevronLeft />
                </Button>
                <span
                  className="fw-bold text-uppercase mx-2 text-center"
                  style={{ minWidth: "150px" }}
                >
                  {format(mesActual, "MMMM yyyy", { locale: es })}
                </span>
                <Button
                  variant="link"
                  className="text-dark p-1"
                  onClick={() => cambiarMes(1)}
                >
                  <FaChevronRight />
                </Button>
              </div>

              {/* P0: Botón principal — "Cargar rol de turnos" o "Recargar" */}
              <Button
                variant={rolCargado ? "primary" : "success"}
                onClick={cargarRolCompleto}
                disabled={cargandoRol}
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: "10px",
                  padding: "10px 24px",
                  fontWeight: "bold",
                  fontSize: rolCargado ? undefined : "1.05rem",
                  boxShadow: rolCargado ? undefined : "0 4px 14px rgba(40,167,69,0.4)",
                }}
              >
                {cargandoRol ? (
                  <>
                    <Spinner animation="border" size="sm" /> Cargando rol...
                  </>
                ) : rolCargado ? (
                  <>
                    <FaSyncAlt /> Recargar
                  </>
                ) : (
                  <>
                    <FaDownload /> Cargar rol de turnos
                  </>
                )}
              </Button>

              {rolCargado && (
                <Button
                  variant="success"
                  onClick={exportarExcel}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    padding: "10px 20px",
                    fontWeight: "bold",
                  }}
                >
                  <FaFileExcel size="18px" /> Excel
                </Button>
              )}
            </Card.Body>
          </Card>

          {/* Filtros — siempre visibles */}
          <div className="bg-white p-4 rounded-4 shadow-sm mb-4 border text-dark">
            <Row className="g-3 align-items-center">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small text-uppercase opacity-75">
                    Grupo (Autorización)
                  </Form.Label>
                  <Form.Select
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    className="shadow-sm rounded-pill"
                    disabled={
                      authorizedGroups.length === 0 &&
                      !isEmpresaAdminRole(usuario?.rol)
                    }
                  >
                    {authorizedGroups.length !== 1 && (
                      <option value="ALL">Todos los grupos</option>
                    )}
                    {authorizedGroups.map((g) => (
                      <option key={`group-filter-${g.id}`} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Form.Select>
                  {authorizedGroups.length === 0 && !isEmpresaAdminRole(usuario?.rol) && (
                    <Form.Text className="text-danger small">
                      Sin grupos autorizados. Contacte al administrador.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small text-uppercase opacity-75">
                    Sede (Filtro)
                  </Form.Label>
                  <Form.Select
                    value={selectedSedeId}
                    onChange={(e) => setSelectedSedeId(e.target.value)}
                    className="shadow-sm rounded-pill"
                  >
                    <option value="ALL">Todas las sedes</option>
                    {sedesEmpresa
                      .filter((s) => scopeSedeIds.includes(String(s.id)))
                      .map((s) => (
                        <option key={`sede-filter-${s.id}`} value={String(s.id)}>
                          {s.nombre || s.id}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small text-uppercase opacity-75">
                    Buscar Colaborador
                  </Form.Label>
                  <InputGroup className="shadow-sm rounded-pill overflow-hidden">
                    <InputGroup.Text className="bg-white border-end-0">
                      <FaSearch className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Nombre o DNI..."
                      className="border-start-0"
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="text-muted small ps-2 mt-1">
                  <em>
                    <FaUserCog className="me-1" />
                    {!rolCargado
                      ? "Seleccione grupo, sede y mes, luego presione 'Cargar rol de turnos'."
                      : "Tip: Haga clic en el nombre del empleado para configurar su ciclo."}
                  </em>
                </div>
              </Col>
            </Row>
          </div>

          {/* P0: Mensaje cuando no se ha cargado el rol */}
          {!rolCargado && !loading && (
            <div className="text-center py-5">
              <FaCalendarAlt size={48} className="text-muted mb-3 opacity-25" />
              <h5 className="text-muted">Rol de turnos no cargado</h5>
              <p className="text-muted small">
                Seleccione el grupo, sede y mes deseado, luego presione el botón{" "}
                <b>"Cargar rol de turnos"</b> para visualizar el calendario.
              </p>
            </div>
          )}

          {/* Loading overlay */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" size="sm" />
              <p className="mt-2 small text-muted">
                {cargandoRol ? "Cargando datos del rol..." : "Cargando contexto..."}
              </p>
            </div>
          )}

          {/* Contenido del rol — solo se muestra cuando está cargado */}
          {rolCargado && !loading && (
            <>
              {/* Leyenda */}
              <div className="px-3 pb-2 pt-1 border-bottom bg-light rounded-top">
                <div
                  className="small text-muted d-flex align-items-center flex-wrap gap-2 mb-1"
                  style={{ fontSize: "0.74rem" }}
                >
                  <b className="me-1">Leyenda Códigos:</b>
                  {EXCEPTION_LEGEND_ORDER.map((code) => {
                    const meta = EXCEPTION_CATALOG[code];
                    if (!meta) return null;
                    return (
                      <span
                        key={`legend-${code}`}
                        className="px-2 py-1 rounded-pill border"
                        style={{
                          backgroundColor: meta.bgColor,
                          color: meta.textColor,
                          borderColor: `${meta.textColor}40`,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {code} - {meta.label}
                      </span>
                    );
                  })}
                </div>
                <div
                  className="small text-muted d-flex align-items-center flex-wrap gap-2"
                  style={{ fontSize: "0.74rem" }}
                >
                  <b className="me-1">Leyenda Marcación:</b>
                  <span
                    className="px-2 py-1 rounded-pill border"
                    style={{
                      backgroundColor: "#d1fae5",
                      color: "#065f46",
                      borderColor: "#10b98155",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    Trabajado con marcación completa
                  </span>
                  <span
                    className="px-2 py-1 rounded-pill border"
                    style={{
                      backgroundColor: "#fef9c3",
                      color: "#854d0e",
                      borderColor: "#eab30866",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    Trabajado con marcación parcial
                  </span>
                </div>
              </div>

              {/* P1: Tabla sin refreshKey */}
              <div
                className="table-responsive border rounded-bottom"
                style={{ maxHeight: "70vh" }}
              >
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 align-middle text-center"
                  style={{ minWidth: "1200px" }}
                >
                  <thead className="sticky-top">
                    <tr
                      style={{
                        height: "42px",
                        background: "linear-gradient(90deg, #0d4f3c 0%, #2c7a2c 100%)",
                      }}
                    >
                      <th
                        style={{
                          width: "36px",
                          minWidth: "36px",
                          padding: "0",
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                          background: "#0d4f3c",
                          color: "white",
                          borderRight: "1px solid rgba(255,255,255,0.15)",
                          verticalAlign: "middle",
                          textAlign: "center",
                          fontSize: "0.65rem",
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          width: "90px",
                          minWidth: "90px",
                          padding: "0 6px",
                          position: "sticky",
                          left: "36px",
                          zIndex: 10,
                          background: "#0d4f3c",
                          color: "white",
                          borderRight: "1px solid rgba(255,255,255,0.15)",
                          verticalAlign: "middle",
                          fontSize: "0.65rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        DNI
                      </th>
                      <th
                        style={{
                          width: "220px",
                          minWidth: "220px",
                          padding: "0 8px",
                          position: "sticky",
                          left: "126px",
                          zIndex: 10,
                          background: "#0d4f3c",
                          color: "white",
                          textAlign: "left",
                          borderRight: "3px solid #f59e0b",
                          verticalAlign: "middle",
                          fontSize: "0.65rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        APELLIDOS Y NOMBRES
                      </th>
                      {diasMes.map((dia) => (
                        <th
                          key={dia.toISOString()}
                          style={{
                            padding: "2px 0",
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            minWidth: "34px",
                            width: "34px",
                            verticalAlign: "middle",
                            background: isToday(dia)
                              ? "#f59e0b"
                              : isWeekend(dia)
                              ? "rgba(255,255,255,0.07)"
                              : "transparent",
                            color: isToday(dia) ? "#101828" : "rgba(255,255,255,0.9)",
                            borderLeft: "none",
                            borderRight: "1px solid rgba(255,255,255,0.07)",
                          }}
                        >
                          <div style={{ opacity: 0.75, fontWeight: 400 }}>
                            {format(dia, "eeeeee", { locale: es })
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                          <div style={{ fontWeight: 700 }}>{format(dia, "d")}</div>
                        </th>
                      ))}
                      <th
                        style={{
                          minWidth: "88px",
                          width: "88px",
                          background: "#0d4f3c",
                          color: "white",
                          fontSize: "0.65rem",
                          verticalAlign: "middle",
                        }}
                      >
                        Trabajados
                      </th>
                      <th
                        style={{
                          minWidth: "92px",
                          width: "92px",
                          background: "#0d4f3c",
                          color: "white",
                          fontSize: "0.65rem",
                          verticalAlign: "middle",
                        }}
                      >
                        Descansados
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: "0.72rem" }}>
                    {empleadosFiltrados.length > 0 ? (
                      empleadosFiltrados.map((emp, idx) => {
                        const dni = String(emp.dni);
                        const empData = shiftGrid[dni];
                        if (!empData) return null;
                        return (
                          <tr
                            key={`${emp.id}-${
                              rolesMap[dni]?.ultimaActualizacion || ""
                            }`}
                            style={{ borderBottom: "1px solid #f1f5f9" }}
                          >
                            <td
                              style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 5,
                                backgroundColor: "#f8fafc",
                                borderRight: "1px solid #e2e8f0",
                                padding: "0 4px",
                                textAlign: "center",
                                color: "#64748b",
                                fontSize: "0.6rem",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                position: "sticky",
                                left: "36px",
                                zIndex: 5,
                                backgroundColor: "#f8fafc",
                                borderRight: "1px solid #e2e8f0",
                                padding: "0 6px",
                                color: "#475569",
                                letterSpacing: "-0.03em",
                                fontSize: "0.7rem",
                              }}
                            >
                              {emp.dni}
                            </td>
                            <td
                              style={{
                                position: "sticky",
                                left: "126px",
                                zIndex: 5,
                                backgroundColor: "#fff",
                                borderRight: "3px solid #e2e8f0",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "220px",
                                padding: "0 8px",
                                color: "#2c7a2c",
                                fontWeight: 600,
                                fontSize: "0.72rem",
                              }}
                              onClick={() => abrirConfiguracion(emp)}
                            >
                              {emp.apellidos} {emp.name}
                            </td>
                            {empData.cells.map((cell) => (
                              <td
                                key={cell.dia.toISOString()}
                                onClick={() => abrirAjuste(emp, cell.dia)}
                                className="hover-cell"
                                title={cell.hoverTitle}
                                style={{
                                  fontSize: "0.68rem",
                                  padding: 0,
                                  height: "32px",
                                  minWidth: "34px",
                                  width: "34px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  backgroundColor: cell.bgColor,
                                  color: cell.textColor,
                                  fontWeight: cell.isWork || cell.isExc ? 700 : 400,
                                  borderRight: "1px solid #f1f5f9",
                                  borderBottom: isToday(cell.dia)
                                    ? "2px solid #f59e0b"
                                    : "1px solid #f1f5f9",
                                  cursor: "pointer",
                                  position: "relative",
                                }}
                              >
                                {cell.info.label}
                              </td>
                            ))}
                            <td
                              style={{
                                fontWeight: 700,
                                color: "#0d4f3c",
                                background: "#f0fdfa",
                              }}
                            >
                              {empData.trabajados}
                            </td>
                            <td
                              style={{
                                fontWeight: 700,
                                color: "#475569",
                                background: "#f8fafc",
                              }}
                            >
                              {empData.descansados}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={diasMes.length + 5}
                          className="py-5 text-muted"
                        >
                          No se encontraron colaboradores.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* Modal de Configuración Individual */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="xl"
        dialogClassName="rol-turnos-modal"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center h5">
            <FaUserCog className="me-2" />
            Configuración de Turno
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 p-md-5">
          {empModal && (
            <div className="mb-4 text-center">
              <div
                className="rounded-circle bg-primary text-white mx-auto d-flex align-items-center justify-content-center mb-2"
                style={{ width: "60px", height: "60px", fontSize: "1.5rem", fontWeight: "bold" }}
              >
                {empModal.name?.charAt(0)}
              </div>
              <h6 className="fw-bold mb-0">
                {empModal.name} {empModal.apellidos}
              </h6>
              <p className="text-muted small">DNI: {empModal.dni}</p>
            </div>
          )}

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-muted text-uppercase">
                Inicio de Rotación
              </Form.Label>
              <Form.Control
                type="date"
                value={configTemp.fechaInicio}
                onChange={(e) =>
                  setConfigTemp((prev) => ({ ...prev, fechaInicio: e.target.value }))
                }
              />
              <Form.Text className="text-muted small">
                Desde qué fecha se cuenta el ciclo.
              </Form.Text>
            </Form.Group>

            <Row className="g-3">
              <Col md={6} lg={4}>
                <Form.Group className="mb-1">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    Días Trabajo
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={configTemp.diasTrabajo}
                    onChange={(e) =>
                      setConfigTemp((prev) => ({
                        ...prev,
                        diasTrabajo: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={4}>
                <Form.Group className="mb-1">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    Días Descanso
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={configTemp.diasDescanso}
                    onChange={(e) =>
                      setConfigTemp((prev) => ({
                        ...prev,
                        diasDescanso: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-1">
                  <Form.Label className="small fw-bold text-muted text-uppercase">
                    Régimen Base
                  </Form.Label>
                  <Form.Select
                    value={configTemp.regimenId || "PERSONALIZADO"}
                    onChange={(e) => {
                      const regimenId = e.target.value;
                      const regimen = regimenesBase.find((r) => r.id === regimenId);
                      if (
                        regimen &&
                        regimen.id !== "PERSONALIZADO" &&
                        regimen.diasTrabajo &&
                        regimen.diasDescanso
                      ) {
                        setConfigTemp((prev) => ({
                          ...prev,
                          regimenId: regimen.id,
                          regimenNombre: regimen.nombre,
                          diasTrabajo: regimen.diasTrabajo,
                          diasDescanso: regimen.diasDescanso,
                        }));
                        return;
                      }
                      setConfigTemp((prev) => ({
                        ...prev,
                        regimenId: "PERSONALIZADO",
                        regimenNombre: "Personalizado",
                      }));
                    }}
                  >
                    {regimenesBase.map((reg) => (
                      <option key={reg.id} value={reg.id}>
                        {reg.id === "PERSONALIZADO"
                          ? "Personalizado"
                          : `${reg.nombre} (${reg.diasTrabajo}x${reg.diasDescanso})`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="mt-3">
              <Form.Text className="text-muted small">
                Selecciona un régimen base o deja personalizado para definir los días
                manualmente.
              </Form.Text>
            </div>

            {regimenSeleccionado &&
              regimenSeleccionado.id !== "PERSONALIZADO" && (
                <Alert variant="secondary" className="py-2 px-3 small mt-2 mb-2">
                  <div>
                    <b>Régimen:</b> {regimenSeleccionado.nombre}
                  </div>
                  <div>
                    <b>Ciclo:</b> {regimenSeleccionado.diasTrabajo}x
                    {regimenSeleccionado.diasDescanso}
                  </div>
                  <div>
                    <b>Horario:</b> {regimenSeleccionado.raw?.hora_inicio || "--:--"} a{" "}
                    {regimenSeleccionado.raw?.hora_fin || "--:--"}
                  </div>
                  <div>
                    <b>Refrigerio:</b>{" "}
                    {regimenSeleccionado.raw?.hora_refrigerio_inicio || "--:--"} a{" "}
                    {regimenSeleccionado.raw?.hora_refrigerio_fin || "--:--"}
                  </div>
                  <div>
                    <b>Horas por día:</b>{" "}
                    {Number(regimenSeleccionado.raw?.horas_por_dia || 0) || "-"}
                  </div>
                  <div>
                    <b>Tolerancia:</b>{" "}
                    {Number(regimenSeleccionado.raw?.tolerancia_minutos || 0) || "-"} min
                  </div>
                  <div>
                    <b>Nocturno:</b>{" "}
                    {regimenSeleccionado.raw?.considera_nocturno ? "Sí" : "No"}
                  </div>
                  {regimenSeleccionado.raw?.descripcion && (
                    <div>
                      <b>Descripción:</b> {regimenSeleccionado.raw.descripcion}
                    </div>
                  )}
                </Alert>
              )}

            <Alert variant="info" className="py-2 px-3 small mt-2">
              Puedes usar régimen base y luego ajustarlo por clima: aumentar/reducir
              trabajo o descanso.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={guardando}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={guardarConfiguracion}
            disabled={guardando}
            className="px-4 shadow-sm"
          >
            {guardando ? (
              <Spinner size="sm" className="me-2" />
            ) : (
              <FaSave className="me-2" />
            )}
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* Modal de Ajuste Rápido (Celda) */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Modal
        show={showAdjModal}
        onHide={() => setShowAdjModal(false)}
        centered
        size="xl"
        dialogClassName="rol-turnos-ajuste-modal"
        enforceFocus={false}
      >
        <Modal.Header closeButton className="bg-dark text-white p-3">
          <Modal.Title className="h6 mb-0">Ajuste de Calendario</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {adjData && (
            <>
              <Row className="g-3">
                <Col lg={4}>
                  <div className="bg-light p-2 border rounded small text-start h-100">
                    <Badge bg="primary" className="mb-2">
                      {format(adjData.dia, "PPPP", { locale: es })}
                    </Badge>
                    <div className="fw-bold">
                      {adjData.emp.name} {adjData.emp.apellidos}
                    </div>
                    <div className="text-muted small">DNI: {adjData.emp.dni}</div>
                    <hr className="my-2" />
                    <div>
                      <b>Fecha Base:</b>{" "}
                      {rolesMap[String(adjData.emp.dni)]?.fechaInicio || "No definida"}
                    </div>
                    <div>
                      <b>Ciclo:</b>{" "}
                      {rolesMap[String(adjData.emp.dni)]?.diasTrabajo}x
                      {rolesMap[String(adjData.emp.dni)]?.diasDescanso}
                    </div>
                    <div>
                      <b>Estado Día:</b>{" "}
                      <span className="text-primary fw-bold">
                        {adjData.info.label}
                      </span>
                    </div>
                    <div>
                      <b>Banco Descanso:</b>{" "}
                      {Number(
                        rolesMap[String(adjData.emp.dni)]?.bancoCompensacion
                          ?.pendienteDescanso || 0
                      )}
                    </div>
                    <div>
                      <b>Banco Pago:</b>{" "}
                      {Number(
                        rolesMap[String(adjData.emp.dni)]?.bancoCompensacion
                          ?.pendientePago || 0
                      )}
                    </div>
                    <hr className="my-2" />
                    <Form.Label className="small fw-bold mb-1">
                      Factor del ajuste
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      className="mb-2"
                      value={adjContext.factor}
                      onChange={(e) =>
                        setAdjContext((prev) => ({
                          ...prev,
                          factor: e.target.value,
                        }))
                      }
                    >
                      <option value="CLIMA">Clima</option>
                      <option value="ACCESO">Acceso/Logística</option>
                      <option value="OPERACION">Necesidad Operativa</option>
                      <option value="SEGURIDAD">Seguridad</option>
                      <option value="SALUD">Salud</option>
                      <option value="OTRO">Otro</option>
                    </Form.Select>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      placeholder="Detalle del factor (ej. lluvia impidió salida de vuelo)"
                      value={adjContext.detalle}
                      onChange={(e) =>
                        setAdjContext((prev) => ({
                          ...prev,
                          detalle: e.target.value,
                        }))
                      }
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="w-100 mt-2"
                      onClick={deshacerUltimoAjuste}
                      disabled={guardando}
                    >
                      Deshacer último ajuste
                    </Button>
                  </div>
                </Col>
                <Col lg={8}>
                  <Row className="g-2">
                    <Col md={6}>
                      <div className="bg-light p-2 rounded-3 text-start border h-100">
                        <Form.Label className="small fw-bold mb-2">
                          Correr Rol
                        </Form.Label>
                        <div className="d-flex gap-2 mb-2">
                          <Form.Control
                            size="sm"
                            type="number"
                            min="1"
                            value={adjShiftDays}
                            onChange={(e) =>
                              setAdjShiftDays(parseInt(e.target.value, 10) || 1)
                            }
                          />
                          <span className="small text-muted align-self-center">
                            día(s)
                          </span>
                        </div>
                        <div className="d-grid gap-1">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => ajustarCalendario("PUSH")}
                          >
                            <FaClock className="me-1" /> Retrasar +N
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => ajustarCalendario("PULL")}
                          >
                            <FaSyncAlt className="me-1" /> Adelantar -N
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light p-2 rounded-3 text-start border h-100">
                        <Form.Label className="small fw-bold mb-2">
                          Anclar Ciclo
                        </Form.Label>
                        <div className="d-grid gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => ajustarCalendario("T1")}
                          >
                            Reiniciar aquí (T1)
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => ajustarCalendario("D1")}
                          >
                            Iniciar descanso aquí (D1)
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light p-2 rounded-3 text-start border h-100">
                        <Form.Label className="small fw-bold mb-2">
                          Excepción del Día
                        </Form.Label>
                        <div className="d-flex flex-wrap gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_T")}
                          >
                            Trabajo
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_D")}
                          >
                            Descanso
                          </Button>
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_C")}
                          >
                            Cese
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_M")}
                          >
                            Desc. Médico
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_V")}
                          >
                            Vacac.
                          </Button>
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_F")}
                          >
                            Falta
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_PG")}
                          >
                            Perm. c/goce
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_PS")}
                          >
                            Perm. s/goce
                          </Button>
                          <Button
                            variant="outline-purple"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_LP")}
                          >
                            Lic. Paternidad
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => ajustarCalendario("EXC_LO")}
                          >
                            Lic. Otros
                          </Button>
                        </div>
                        {adjData?.info?.excepcion && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger w-100 mt-1 p-0"
                            onClick={() => ajustarCalendario("EXC_CLEAR")}
                          >
                            Quitar excepción
                          </Button>
                        )}
                        <div
                          className="small text-muted mt-2"
                          style={{ fontSize: "0.72rem" }}
                        >
                          Códigos: <b>T</b> Trabajo, <b>N</b> T. Noche, <b>D</b>{" "}
                          Descanso, <b>C</b> Cese, <b>M</b> Desc. Médico, <b>V</b>{" "}
                          Vacaciones, <b>F</b> Falta, <b>PG</b> Permiso con goce,{" "}
                          <b>PS</b> Permiso sin goce, <b>LP</b> Lic. Paternidad,{" "}
                          <b>LO</b> Lic. Otros.
                        </div>
                        <div
                          className="small text-danger mt-1"
                          style={{ fontSize: "0.72rem" }}
                        >
                          Para <b>LO</b> se abrirá un modal con comentario obligatorio
                          (si ya existe, se mostrará para editar).
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light p-2 rounded-3 text-start border h-100">
                        <Form.Label className="small fw-bold mb-2">
                          Forzar Posición
                        </Form.Label>
                        <div className="d-flex gap-1 mb-2">
                          <Form.Select
                            size="sm"
                            value={adjConfig.type}
                            onChange={(e) =>
                              setAdjConfig((prev) => ({
                                ...prev,
                                type: e.target.value,
                                num: 1,
                              }))
                            }
                          >
                            <option value="Trabajo">T (Trabajo)</option>
                            <option value="Descanso">D (Descanso)</option>
                          </Form.Select>
                          <Form.Control
                            size="sm"
                            type="number"
                            min="1"
                            value={adjConfig.num}
                            onChange={(e) =>
                              setAdjConfig((prev) => ({
                                ...prev,
                                num: parseInt(e.target.value, 10) || 1,
                              }))
                            }
                            style={{ width: "84px" }}
                          />
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-100"
                          onClick={() => ajustarCalendario("CUSTOM")}
                        >
                          Sincronizar a{" "}
                          {adjConfig.type === "Trabajo" ? "T" : "D"}
                          {adjConfig.num}
                        </Button>
                      </div>
                    </Col>
                    <Col md={12}>
                      <div className="bg-light p-2 rounded-3 text-start border">
                        <Form.Label className="small fw-bold mb-2">
                          Ajuste por Clima / Factores
                        </Form.Label>
                        <Row className="g-2">
                          <Col md={3}>
                            <Form.Control
                              size="sm"
                              type="number"
                              min="1"
                              value={adjFlex.dias}
                              onChange={(e) =>
                                setAdjFlex((prev) => ({
                                  ...prev,
                                  dias: parseInt(e.target.value, 10) || 1,
                                }))
                              }
                            />
                          </Col>
                          <Col md={5}>
                            <Form.Select
                              size="sm"
                              value={adjFlex.compensacion}
                              onChange={(e) =>
                                setAdjFlex((prev) => ({
                                  ...prev,
                                  compensacion: e.target.value,
                                }))
                              }
                            >
                              <option value="DESCANSO">
                                Compensar con descanso
                              </option>
                              <option value="PAGO">Compensar con pago</option>
                              <option value="NINGUNA">Sin compensación</option>
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Form.Control
                              size="sm"
                              placeholder="Motivo breve"
                              value={adjFlex.motivo}
                              onChange={(e) =>
                                setAdjFlex((prev) => ({
                                  ...prev,
                                  motivo: e.target.value,
                                }))
                              }
                            />
                          </Col>
                        </Row>
                        <div className="d-grid gap-1 mt-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              aplicarAjusteFlexible("AUMENTAR_TRABAJO")
                            }
                          >
                            Aumentar trabajo (+)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() =>
                              aplicarAjusteFlexible("REDUCIR_TRABAJO")
                            }
                          >
                            Reducir trabajo (-)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() =>
                              aplicarAjusteFlexible("AUMENTAR_DESCANSO")
                            }
                          >
                            Aumentar descanso (+)
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() =>
                              aplicarAjusteFlexible("REDUCIR_DESCANSO")
                            }
                          >
                            Reducir descanso (-)
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
          .hover-cell { cursor: pointer; transition: background 0.12s; }
          .hover-cell:hover { background-color: #e0f2fe !important; }
          .rol-turnos-modal .modal-dialog { max-width: 980px; }
          .rol-turnos-ajuste-modal .modal-dialog { max-width: 1240px; }
          .table-responsive::-webkit-scrollbar { height: 5px; width: 5px; }
          .table-responsive::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          .table-responsive::-webkit-scrollbar-track { background: #f1f5f9; }
          thead.sticky-top { position: sticky; top: 0; z-index: 30; }
          table { border-collapse: collapse; }
      `}</style>
    </Container>
  );
};

export default RolTurnos;
